import { useCallback } from 'react';
import {
    Account,
    Case,
    CaseData,
    CaseTemplate,
    Contact,
    EngagementTemplate,
    Notification,
    Party,
    User,
    UserAccount,
} from '../data/types';
import { useAccountActions } from '../redux/actions/accountActions';
import { useAccountState } from '../redux/reducers/accountReducer';
import { useAccountSignalR } from './useAccountSignalR';
import { useUserSignalR } from './useUserSignalR';
import { useUserActions } from '../redux/actions/userActions';
import { useCaseDataActions } from '../redux/actions/caseDataActions';
import { useCaseDataSignalR } from './useCaseDataSignalR';
import { useCaseActions } from '../redux/actions/caseActions';
import { useCaseSignalR } from './useCaseSignalR';
import { usePartyActions } from '../redux/actions/partyActions';
import { usePartySignalR } from './usePartySignalR';
import { useEngagementTemplateSignalR } from './useEngagementTemplateSignalR';
import { useEngagementTemplateActions } from '../redux/actions/engagementTemplateActions';
import { useCaseTemplateSignalR } from './useCaseTemplateSignalR';
import { useCaseTemplateActions } from '../redux/actions/caseTemplateActions';
import { useNotificationSignalR } from './useNotificationSignalR';
import { useNotificationState } from '../redux/reducers/notificationReducer';
import { useNotificationActions } from '../redux/actions/notificationActions';

export const useAccountLevelSignalR = () => {
    const accountActions = useAccountActions();
    const { primaryAccount, userAccounts } = useAccountState();
    const userActions = useUserActions();
    const caseDataStateActions = useCaseDataActions();
    const caseStateActions = useCaseActions();
    const partyActions = usePartyActions();
    const engagementTemplateActions = useEngagementTemplateActions();
    const caseTemplateStateActions = useCaseTemplateActions();
    const notificationActions = useNotificationActions();
    const notificationState = useNotificationState();

    //Account SignalR
    const onUpsertAccount = useCallback(
        (acct: Account) => {
            if (primaryAccount?.account.accountId != acct.accountId) return;

            accountActions.setPrimaryAccount({ ...primaryAccount, account: acct } as UserAccount);
        },
        [accountActions, primaryAccount],
    );
    const onStorageSizeChange = useCallback(
        (accountId: string, storageSize: number) => {
            if (primaryAccount?.account.accountId == accountId) {
                accountActions.setPrimaryAccount({
                    ...primaryAccount,
                    accountStorageSize: storageSize,
                } as UserAccount);
            } else {
                const acctToUpdate = userAccounts?.find((acct) => acct.account.accountId == accountId);
                if (acctToUpdate) {
                    const updatedAcct = {
                        ...acctToUpdate,
                        accountStorageSize: storageSize,
                    } as UserAccount;
                    accountActions.replaceUserInAccount(updatedAcct.accountUsers[0]);
                }
            }
        },
        [accountActions, userAccounts, primaryAccount],
    );
    useAccountSignalR({ primaryAccount, onUpsertAccount, onStorageSizeChange });

    //User SignalR
    const onUpsertUser = (users: User[]) => {
        users.forEach((user) => {
            if (user.accountId !== primaryAccount?.account.accountId) return;
            userActions.addOrReplaceUser(user);
            accountActions.replaceUserInAccount(user);
        });
    };
    useUserSignalR({ primaryAccount, onUpsertUser });

    //Case Data SignalR
    const onUpsertCaseData = (caseData: CaseData) => {
        if (primaryAccount?.account.accountId !== caseData.accountId) return;

        caseDataStateActions.setCaseData(caseData);
    };
    useCaseDataSignalR({ primaryAccount, onUpsertCaseData });

    //Case SignalR
    const onUpsertCase = (cases: Case[]) => {
        cases.forEach((caseObj) => {
            if (primaryAccount?.account?.accountId !== caseObj.accountId) return;
            console.log('Received SignalR update for case:', caseObj.caseId);
            caseStateActions.updateCase(caseObj);
        });
    };
    useCaseSignalR({ primaryAccount, onUpsertCase });

    //Party SignalR
    const onUpsertParty = (parties: Party[]) => {
        parties.forEach((party) => {
            if (primaryAccount?.account?.accountId !== party.accountId) return;

            partyActions.addOrReplaceParty(party);
        });
    };
    usePartySignalR({ primaryAccount, onUpsertParty });

    //Engagement Template SignalR
    const onUpsertEngagementTemplate = (engTemplate: EngagementTemplate) => {
        if (primaryAccount?.account?.accountId !== engTemplate.accountId) return;

        if (engTemplate.isDeleted) {
            engagementTemplateActions.removeEngagementTemplate(engTemplate);
            return;
        }
        engagementTemplateActions.addOrReplaceEngagementTemplate(engTemplate);
    };
    useEngagementTemplateSignalR({ primaryAccount, onUpsertEngagementTemplate });

    //Case Template SignalR
    const onUpsertCaseTemplate = (caseTemplate: CaseTemplate) => {
        if (primaryAccount?.account?.accountId !== caseTemplate.accountId) return;

        if (caseTemplate.isDeleted) {
            caseTemplateStateActions.removeCaseTemplate(caseTemplate.templateId as string);
            return;
        }
        caseTemplateStateActions.addOrReplaceCaseTemplate(caseTemplate);
    };
    useCaseTemplateSignalR({ primaryAccount, onUpsertCaseTemplate });

    //Notification SignalR
    const onUpsertNotification = (notifications: Notification[]) => {
        notifications.forEach((notification) => {
            if (
                primaryAccount?.account?.accountId !== notification.accountId ||
                primaryAccount?.user?.userId !== notification.userId
            )
                return;

            if (notification.isDeleted) {
                notificationActions.deleteNotification(notification.notificationId as string);
                return;
            }
            if (
                !!notification.viewedOn &&
                !notificationState.notifications.some((n: Notification) => n.notificationId === notification.notificationId)
            ) {
                //Don't add notification if it's already viewed and no longer exist in state (prevents adding back deleted notifications when they are marked viewed in another session)
                return;
            }
            notificationActions.addOrReplaceNotification(notification);
        });
    };
    useNotificationSignalR({ primaryAccount, onUpsertNotification });
};
